import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Page, Layout, Card, Filters } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { fetchOrders, setFilterValue } from "features/orders/orders.slice";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import { AppDispatch, RootState } from "configureStore";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import { OrderProductLines } from "features/orders/order-product-lines";
import { buildOrderPhoneColumns } from "features/orders/order-phone-columns";
import useIsPhone from "features/files/use-is-phone";

type Params = {
  companyId: string;
};

function Orders() {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams<Params>();
  const isPhone = useIsPhone();

  const {
    ordersReducer: { ordersCollection, filtersValue, isLoading },
  } = useSelector((state: RootState) => state);

  const handleCreateOrder = () => {
    navigate(`/app/${companyId}/orders/new`);
  };

  const disambiguateLabel = (key: string, value: string) => {
    switch (key) {
      case "textSearch":
        return `${t("search")}: ${t(value)}`;
      default:
        return value;
    }
  };

  useEffect(() => {
    if (companyId) {
      dispatch(fetchOrders({ filtersValue, companyId }));
    }
  }, [dispatch, filtersValue, companyId]);

  const handleFilterChange = (key: string, value: string | null) => {
    dispatch(setFilterValue({ key, value }));
  };

  const handleQueryChange = (value: string) => {
    dispatch(setFilterValue({ key: "textSearch", value }));
  };

  const handleQueryValueRemove = () => {
    dispatch(setFilterValue({ key: "textSearch", value: "" }));
  };

  const handleClearAll = () => {
    // Optional: clear all filters if needed
  };

  const appliedFilters = Object.keys(filtersValue)
    .filter((key) => filtersValue[key]?.length > 0)
    .map((key) => ({
      key,
      label: disambiguateLabel(key, filtersValue[key]),
      onRemove: () => handleFilterChange(key, null),
    }));

  const onRowClick = ({ id }: { id: string }) => {
    navigate(`/app/${companyId}/orders/${id}`);
  };

  const filters: any[] = [];

  const collection =
    ordersCollection.map((item: any) => ({
      id: item.id,
      date: item.date,
      totalPrice: item.totalPrice,
      responsibleUser: item.responsibleUser?.name || "",
      companyId: item.companyId,
      customerId: item.customerId,
      customerName: item.customer?.name || "",
      products: item.products,
      signature: item.files.length ? item.files[0].signStatus : "not_initiate",
      tone: item.files.length
        ? item.files[0].signStatus === "pending"
          ? "attention"
          : item.files[0].signStatus === "signed"
          ? "success"
          : "info"
        : "info",
      number: item._number,
      internal_order_number: item.number,
    })) || [];

  const baseColumns: Column[] = [
    {
      key: "internal_order_number",
      name: t("internal_order_number"),
      onClick: onRowClick,
    },
    {
      key: "date",
      name: t("date"),
      type: "clickabledate",
      onClick: onRowClick,
    },
    {
      key: "number",
      name: t("number"),
      onClick: onRowClick,
    },
    {
      key: "customerName",
      name: t("customer"),
      onClick: onRowClick,
    },
    {
      key: "products",
      type: "custom",
      name: t("products"),
      render: (row: any) => <OrderProductLines products={row.products} />,
    },
    {
      key: "totalPrice",
      name: t("totalPrice"),
      type: "currency",
      onClick: onRowClick,
    },
    {
      key: "signature",
      name: t("signature"),
      translationRequired: true,
      type: "badge",
      onClick: onRowClick,
    },
    {
      key: "responsibleUser",
      name: t("user"),
      onClick: onRowClick,
    },
  ];
  const columns = isPhone
    ? buildOrderPhoneColumns({ showCustomer: true })
    : baseColumns.filter((column) => column.key !== "status");

  return (
    <Layout>
      <Layout.Section>
        <Page
          fullWidth
          primaryAction={{
            content: t("create_order"),
            onAction: handleCreateOrder,
          }}
          title={t("orders")}
          titleMetadata={<AuditLogPopover subjectType="Order" />}
        >
          <Card>
            <div style={{ padding: "16px", display: "flex" }}>
              <div style={{ flex: 1 }}>
                <Filters
                  queryValue={filtersValue.textSearch}
                  queryPlaceholder={t("orders_search_placeholder")}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onQueryChange={handleQueryChange}
                  onQueryClear={handleQueryValueRemove}
                  onClearAll={handleClearAll}
                />
              </div>
            </div>
            {isLoading && collection.length === 0 ? (
              <DataTableSkeleton rows={10} columns={columns.length} />
            ) : (
              <DataTable
                collection={collection}
                columns={columns}
                // The table opens an order from its cells; a phone's card has
                // no cells to tap, so the whole card is the link.
                rowUrl={
                  isPhone
                    ? (order) => `/app/${companyId}/orders/${order.id}`
                    : undefined
                }
              />
            )}
          </Card>
        </Page>
      </Layout.Section>
    </Layout>
  );
}

export default Orders;
